import type { TtqibCanvasObject } from './types';
import { TTQIB_CARD_WIDTH, estimateWholeHeight } from './ttqibObjectLayout';

export const TTQIB_ASSOC_LANE_GAP = 48;

export type TtqParticleAssociation = {
  sourceParticleId: string;
  linkedParticleId: string;
};

/** 将关联颗粒排到源题右侧泳道（全卡、可品题） */
export function applyAssociationLaneLayout(
  objects: TtqibCanvasObject[],
  associations: readonly TtqParticleAssociation[],
): TtqibCanvasObject[] {
  if (!associations.length) return objects;

  const bySource = new Map<string, string[]>();
  for (const edge of associations) {
    const sourceId = String(edge.sourceParticleId || '').trim();
    const linkedId = String(edge.linkedParticleId || '').trim();
    if (!sourceId || !linkedId || sourceId === linkedId) continue;
    const bucket = bySource.get(sourceId) ?? [];
    if (!bucket.includes(linkedId)) bucket.push(linkedId);
    bySource.set(sourceId, bucket);
  }
  if (bySource.size === 0) return objects;

  const wholeByParticle = new Map(
    objects.filter((item) => item.type === 'particle-whole').map((item) => [item.particleId, item]),
  );

  const nextPos = new Map<string, { x: number; y: number; layerId: string; width?: number }>();
  for (const [sourceId, linkedIds] of bySource) {
    const sourceWhole = wholeByParticle.get(sourceId);
    if (!sourceWhole) continue;
    const sourceW = sourceWhole.width ?? TTQIB_CARD_WIDTH;
    linkedIds.forEach((linkedId, index) => {
      nextPos.set(linkedId, {
        x: sourceWhole.x + sourceW + TTQIB_ASSOC_LANE_GAP + index * (TTQIB_CARD_WIDTH + TTQIB_ASSOC_LANE_GAP),
        y: sourceWhole.y,
        layerId: sourceWhole.layerId,
        width: TTQIB_CARD_WIDTH,
      });
    });
  }

  if (nextPos.size === 0) return objects;

  return objects.map((object) => {
    const stacked = nextPos.get(object.particleId);
    if (!stacked) return object;
    return {
      ...object,
      layerId: stacked.layerId,
      x: stacked.x,
      y: stacked.y,
      width: object.type === 'particle-whole' ? stacked.width : object.width,
      height: object.type === 'particle-whole'
        ? (object.height ?? estimateWholeHeight())
        : object.height,
      hidden: false,
    };
  });
}

export function listLinkedRefsForSource(
  associations: readonly TtqParticleAssociation[],
  sourceParticleId: string,
): string[] {
  return associations
    .filter((edge) => edge.sourceParticleId === sourceParticleId)
    .map((edge) => edge.linkedParticleId)
    .filter(Boolean);
}

export function upsertAssociations(
  current: readonly TtqParticleAssociation[],
  sourceParticleId: string,
  linkedParticleIds: readonly string[],
): TtqParticleAssociation[] {
  const next = current.filter((edge) => !(
    edge.sourceParticleId === sourceParticleId
    && linkedParticleIds.includes(edge.linkedParticleId)
  ));
  for (const linkedId of linkedParticleIds) {
    if (!linkedId || linkedId === sourceParticleId) continue;
    next.push({ sourceParticleId, linkedParticleId: linkedId });
  }
  return next;
}
